import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ScanBarcode, Truck } from "lucide-react";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { apiFetch } from "../api/apiFetch";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FlowLoader from "../components/FlowLoader";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import FitWord from "../components/FitWord";
import { carrierClass, carrierDisplayName, type ClosedPallet } from "./dispatch-unload";
import "../styles/scansheet.css";
import "../styles/dispatch-unload.css";

// ---------------------------------------------------------------------------
// Dispatch › Load lorry (Alex, 2026-10-19)
//
// The forklift driver's end of the parcel trail. A pallet closed at the
// conveyor unload point carries a pre-printed label (PD-APC-12); the
// driver picks the courier whose lorry is being loaded, then scans each
// pallet's label (POST /dispatch/load/scan-pallet). The Portal stamps who
// loaded it and when, so every parcel on it reads shipped → pallet →
// lorry. Another courier's pallet, a label that never closed a pallet,
// another site's pallet or one already loaded is refused with a card.
//
// The courier buttons sit across the top (tap another to switch lorry),
// and the closed pallets still on the floor (GET /dispatch/unload/
// pallets) are shown under the scan in one column per courier, each
// headed by how many pallets that lorry has to take, so nothing gets
// left behind.
// ---------------------------------------------------------------------------

type Card =
    | { kind: "wrong_courier"; label: string; palletCarrier: string; loadingCarrier: string }
    | { kind: "already_loaded"; label: string; at: string | null; by: string | null }
    | { kind: "label_unused"; label: string }
    | { kind: "wrong_warehouse"; label: string; warehouse: string };

const readWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        const parsed = raw ? JSON.parse(raw) : null;
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : "";
    } catch {
        return "";
    }
};

const fmtTime = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const DispatchLoad = () => {
    usePageTitle("dispatch_load");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const warehouse = readWarehouse();

    const [carrier, setCarrier] = useState<string | null>(null);
    const [scan, setScan] = useState("");
    const [loading, setLoading] = useState(false);
    const [card, setCard] = useState<Card | null>(null);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [bayCarriers, setBayCarriers] = useState<string[]>([]);
    const [awaiting, setAwaiting] = useState<ClosedPallet[]>([]);
    const [loadedToday, setLoadedToday] = useState<ClosedPallet[]>([]);
    const [session, setSession] = useState<ClosedPallet[]>([]);

    const flash = useCallback((status: "success" | "error", message?: string) => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        setErrorMessage(message);
        statusTimerRef.current = setTimeout(() => { setScanStatus("idle"); setErrorMessage(undefined); }, status === "error" ? 900 : 500);
    }, []);

    // The couriers with a pallet position here (the lorries that come), and
    // the closed pallets waiting on the floor.
    const refresh = useCallback(async () => {
        try {
            const [baysRes, palletsRes] = await Promise.all([
                apiFetch(`/dispatch/unload/bays?warehouse=${encodeURIComponent(warehouse)}`, {}),
                apiFetch(`/dispatch/unload/pallets?warehouse=${encodeURIComponent(warehouse)}`, {}),
            ]);
            if (baysRes.status === 401 || palletsRes.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (baysRes.ok) {
                const data = await baysRes.json();
                const codes = new Set<string>();
                // Safety-check positions (Royal Mail) have no pallets to load.
                for (const b of (Array.isArray(data.bays) ? data.bays : []) as Array<{ carrier_code: string; pallets?: boolean }>) {
                    if (b.pallets !== false) codes.add(String(b.carrier_code).toLowerCase());
                }
                setBayCarriers([...codes]);
            }
            if (palletsRes.ok) {
                const data = await palletsRes.json();
                setAwaiting(Array.isArray(data.awaiting) ? data.awaiting : []);
                setLoadedToday(Array.isArray(data.loaded_today) ? data.loaded_today : []);
            }
        } catch {
            /* the lists are a courtesy; the scan itself is authoritative */
        }
    }, [warehouse, navigate]);

    useEffect(() => { refresh(); }, [refresh]);

    // Courier pills: every courier with a position here, plus any courier
    // with a closed pallet waiting (a pallet outlives a removed position).
    // Collections are picked up from the warehouse, never loaded on a lorry.
    const carriers = useMemo(() => {
        const codes = new Set<string>(bayCarriers);
        for (const p of awaiting) codes.add(String(p.carrier_code).toLowerCase());
        codes.delete("collection");
        return [...codes].sort();
    }, [bayCarriers, awaiting]);

    const loadPallet = useCallback(async (label: string) => {
        if (!carrier) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/dispatch/load/scan-pallet`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ label, carrier_code: carrier }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flash("error", t("server_error"));
                return;
            }
            const data = await res.json();
            if (data.ok) {
                const p = data.pallet as ClosedPallet;
                playSuccess();
                flash("success");
                setSession((s) => [p, ...s]);
                setAwaiting((list) => list.filter((x) => x.id !== p.id));
                setLoadedToday((list) => [p, ...list]);
                return;
            }
            playError();
            const scanned = String(data.pallet?.label ?? data.label ?? label);
            switch (data.reason) {
                case "label_invalid":
                    flash("error", data.is_shipping_label ? t("load_not_a_pallet_parcel") : data.is_floor_barcode ? t("load_not_a_pallet_floor") : t("load_not_a_pallet"));
                    return;
                case "wrong_courier":
                    flash("error");
                    setCard({
                        kind: "wrong_courier", label: scanned,
                        palletCarrier: String(data.pallet?.carrier_name ?? carrierDisplayName(data.pallet?.carrier_code)),
                        loadingCarrier: String(data.loading_carrier_name ?? carrierDisplayName(carrier)),
                    });
                    return;
                case "already_loaded":
                    flash("error");
                    setCard({ kind: "already_loaded", label: scanned, at: data.pallet?.loaded_at ?? null, by: data.pallet?.loaded_by ?? null });
                    return;
                case "label_unused":
                    flash("error");
                    setCard({ kind: "label_unused", label: scanned });
                    return;
                case "wrong_warehouse":
                    flash("error");
                    setCard({ kind: "wrong_warehouse", label: scanned, warehouse: String(data.pallet_warehouse ?? "") });
                    return;
                default:
                    flash("error", t("scan_failed"));
            }
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [carrier, flash, navigate, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        setScan("");
        if (!val || loading) return;
        if (card) setCard(null);
        if (!carrier) {
            playError();
            flash("error", t("load_pick_courier"));
            return;
        }
        loadPallet(val);
    }, [scan, loading, card, carrier, loadPallet, flash, t]);

    const carrierName = carrier ? carrierDisplayName(carrier) : "";
    const loadedCount = carrier ? loadedToday.filter((p) => String(p.carrier_code).toLowerCase() === carrier).length : loadedToday.length;

    return (
        <div
            className={`scan-panel unload-panel ${carrier ? `unload-panel--carrier ${carrierClass(carrier)}` : ""}`}
            style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}
        >
            <ScanFlashOverlay active={scanStatus === "error"} message={errorMessage} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("dispatch_load")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/dispatch/menu")} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {/* Courier buttons stay at the top: tapping another courier
                switches the lorry being loaded. */}
            <div className={`load-courier-bar ${carrier ? "load-courier-bar--chosen" : ""}`} data-testid="load-pick-courier">
                {carriers.map((code) => (
                    <button
                        key={code}
                        type="button"
                        className={`load-courier-btn ${carrierClass(code)} ${code === carrier ? "load-courier-btn--active" : ""}`}
                        aria-pressed={code === carrier}
                        onClick={() => { setCarrier(code); setCard(null); }}
                        disabled={loading}
                    >
                        {carrierDisplayName(code)}
                    </button>
                ))}
            </div>

            <div className="scan-centre">
                {carrier && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}
                {loading && <FlowLoader />}

                {!carrier && !loading && (
                    <div className="unload-target load-pick">
                        <Truck size={40} strokeWidth={1.5} />
                        <span className="unload-target-label">{t("load_pick_courier")}</span>
                    </div>
                )}

                {carrier && !loading && (
                    <div className="unload-target" data-testid="load-target">
                        <span className="unload-target-label">{t("load_loading", { carrier: carrierName }).toUpperCase()}</span>
                        <div className={`scan-command unload-target-carrier ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("load_scan_pallet").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <span className="unload-target-hint">
                            <ScanBarcode size={16} />
                            {t("load_loaded_today", { count: String(loadedCount) })}
                        </span>
                    </div>
                )}
            </div>

            {card && (
                <div className={`unload-card-overlay unload-card-overlay--${card.kind === "already_loaded" ? "warn" : "stop"}`} role="alertdialog">
                    <div className="unload-card">
                        <h2 className="unload-card-title">
                            {card.kind === "wrong_courier" && t("load_wrong_courier_title")}
                            {card.kind === "already_loaded" && t("load_already_loaded_title")}
                            {card.kind === "label_unused" && t("load_label_unused_title")}
                            {card.kind === "wrong_warehouse" && t("load_wrong_warehouse_title")}
                        </h2>
                        <p className="unload-card-text">
                            {card.kind === "wrong_courier" && t("load_wrong_courier_detail", { label: card.label, pallet_carrier: card.palletCarrier, loading_carrier: card.loadingCarrier })}
                            {card.kind === "already_loaded" && t("load_already_loaded_detail", { label: card.label, time: fmtTime(card.at), name: card.by ?? "" })}
                            {card.kind === "label_unused" && t("load_label_unused_detail", { label: card.label })}
                            {card.kind === "wrong_warehouse" && t("load_wrong_warehouse_detail", { label: card.label, warehouse: card.warehouse })}
                        </p>
                        <div className="unload-card-actions">
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCard(null)}>
                                {t("unload_ok")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="dispatch-stats-bar unload-session" aria-label={t("session_stats")}>
                <p className="dispatch-stats-title">{t("session_stats")}</p>
                <div className="dispatch-stats-row">
                    <span className="dispatch-stats-item">{session.length} {t("load_session_loaded")}</span>
                    {session.slice(0, 3).map((p) => (
                        <span key={p.id} className={`unload-session-chip ${carrierClass(p.carrier_code)}`}>
                            {t("load_loaded_detail", { label: String(p.label ?? ""), count: String(p.parcel_count ?? 0) })}
                        </span>
                    ))}
                </div>
            </div>

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading}
            />
        </div>
    );
};

export default DispatchLoad;
